import { ChangeEvent, FocusEvent, KeyboardEvent, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';

import { Icon } from '@components/icon';
import { useSwiperNavigation } from '@components/utils';
import 'swiper/css';

import './SearchBar.scss';

const EDGE_TOLERANCE_PX = 5;

type SwiperEdgeState = { isBeginning: boolean; isEnd: boolean };

/** Reliable edge detection for slidesPerView="auto" where swiper.isEnd is often false. */
const getEdgeState = (swiper: SwiperType): SwiperEdgeState => {
  if (!swiper.slides?.length) {
    return { isBeginning: swiper.isBeginning, isEnd: swiper.isEnd };
  }

  let minTranslate: number;
  let maxTranslate: number;

  try {
    minTranslate = swiper.minTranslate();
    maxTranslate = swiper.maxTranslate();
  } catch {
    return { isBeginning: swiper.isBeginning, isEnd: swiper.isEnd };
  }

  if (Math.abs(maxTranslate - minTranslate) <= EDGE_TOLERANCE_PX) {
    return { isBeginning: true, isEnd: true };
  }

  const isBeginning =
    swiper.isBeginning || Math.abs(swiper.translate - minTranslate) <= EDGE_TOLERANCE_PX;
  const isEndByTranslate = Math.abs(swiper.translate - maxTranslate) <= EDGE_TOLERANCE_PX;
  const lastSlide = swiper.slides[swiper.slides.length - 1] as HTMLElement | undefined;
  const containerRect = swiper.el?.getBoundingClientRect();
  const isEndByDom =
    lastSlide !== undefined &&
    containerRect !== undefined &&
    lastSlide.getBoundingClientRect().right <= containerRect.right + EDGE_TOLERANCE_PX;

  return { isBeginning, isEnd: isEndByTranslate || isEndByDom };
};

export interface SearchBarAriaLabels {
  /** Accessible label for the search input */
  input?: string;
  /** Accessible label for the clear button */
  clearButton?: string;
  /** Accessible label for the previous suggestions button */
  prevSuggestions?: string;
  /** Accessible label for the next suggestions button */
  nextSuggestions?: string;
}

export interface SearchBarProps {
  /** Current value of the search input (for controlled usage) */
  value?: string;
  /** Default value of the search input (for uncontrolled usage) */
  defaultValue?: string;
  /** Placeholder text for the search input */
  placeholder?: string;
  /** Accessible labels for the internal controls */
  ariaLabels?: SearchBarAriaLabels;
  /** Called when the value changes */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /** Called when the user submits the search (presses Enter or clicks search icon) or selects a suggestion chip (only on select, not on deselect) */
  onSearch?: (value: string) => void;
  /**
   * Called on every user query change (each key press) so the parent
   * can trigger an autocomplete HTTP request.
   */
  onQueryChange?: (query: string) => void;
  /** Suggestions to display in the autocomplete row */
  suggestions?: string[];
  /**
   * Called when the user toggles a suggestion filter.
   * Receives the currently selected suggestion label, or null when deselected.
   */
  onSuggestionSelect?: (suggestion: string | null) => void;
  /** Test ID rendered as `data-ci` on every suggestion chip */
  suggestionDataCi?: string;
  /** Called when input receives focus */
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  /** Additional classname for the root element */
  className?: string;
}

/**
 * SearchBar component - search field with horizontal autocomplete row.
 * Suggestion chips act as toggle filters: they do not change the input value,
 * but notify the parent via `onSuggestionSelect`. When a chip is selected and
 * `onSearch` is provided, the search is also triggered with the suggestion text
 * so that the parent's search handler (which typically handles URL encoding)
 * is invoked — avoiding issues with special characters such as `&` in URLs.
 *
 * @param value - Current value of the search input (for controlled usage).
 * @param defaultValue - Default value of the search input (for uncontrolled usage).
 * @param placeholder - Placeholder text for the search input.
 * @param ariaLabels - Accessible labels for the internal controls.
 * @param onChange - Called when the value changes.
 * @param onSearch - Called when the user submits the search (presses Enter or clicks search icon) or selects a suggestion chip (only on select, not on deselect).
 * @param onQueryChange - Called when the query changes so the parent can fetch autocomplete suggestions.
 * @param suggestions - Suggestions to display in the horizontal autocomplete row.
 * @param onSuggestionSelect - Called when the user toggles a suggestion filter. Receives the
 * currently selected suggestion label, or null when the selection is cleared.
 * @param suggestionDataCi - Test ID rendered as `data-ci` on every suggestion chip.
 * @param onFocus - Called when input receives focus.
 * @param className - Additional classname for the root element.
 */
export const SearchBar: React.FC<SearchBarProps> = ({
  value,
  defaultValue = '',
  placeholder = 'Search',
  ariaLabels,
  onChange,
  onSearch,
  onQueryChange,
  suggestions = [],
  onSuggestionSelect,
  suggestionDataCi,
  onFocus,
  className,
  ...rest
}) => {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState<string>(defaultValue);
  const [isSuggestionsVisible, setIsSuggestionsVisible] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState<string | null>(null);
  const [edgeState, setEdgeState] = useState({ isBeginning: true, isEnd: false });

  const inputValue = isControlled ? value : internalValue;
  const { isSwiperDisabled, handleOnResize, swiperRef } = useSwiperNavigation({
    updateDependencies: [suggestions, isSuggestionsVisible],
    shouldUpdate: isSuggestionsVisible,
  });

  // English defaults, overridden by provided ariaLabels
  const {
    input: inputAriaLabel = 'Search',
    clearButton: clearButtonAriaLabel = 'Clear search',
    prevSuggestions: prevSuggestionsAriaLabel = 'Scroll suggestions left',
    nextSuggestions: nextSuggestionsAriaLabel = 'Scroll suggestions right',
  } = ariaLabels ?? {};

  useEffect(() => {
    const shouldShowSuggestions = Boolean(inputValue) && suggestions.length > 0;

    setIsSuggestionsVisible(shouldShowSuggestions);

    if (!shouldShowSuggestions && highlightedIndex !== null) {
      setHighlightedIndex(null);
    }
  }, [inputValue, suggestions, highlightedIndex]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (!isControlled) {
      setInternalValue(event.target.value);
    }

    if (onChange) {
      onChange(event);
    }

    if (onQueryChange) {
      onQueryChange(event.target.value);
    }
  };

  const triggerSearch = (query: string): void => {
    if (onSearch) {
      onSearch(query);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (!isSuggestionsVisible || suggestions.length === 0) {
      if (event.key === 'Enter') {
        triggerSearch(inputValue ?? '');
      }
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      if (highlightedIndex !== null) {
        const suggestion = suggestions[highlightedIndex];
        handleSuggestionSelect(suggestion);
      } else {
        triggerSearch(inputValue ?? '');
      }
    } else if (event.key === 'Escape') {
      setIsSuggestionsVisible(false);
      setHighlightedIndex(null);
    }
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>): void => {
    if (onFocus) {
      onFocus(event);
    }

    if (suggestions.length > 0 && inputValue) {
      setIsSuggestionsVisible(true);
    }
  };

  const handleClear = (): void => {
    if (!isControlled) {
      setInternalValue('');
    }

    if (onQueryChange) {
      onQueryChange('');
    }

    if (onSearch) {
      onSearch('');
    }

    setIsSuggestionsVisible(false);
    setHighlightedIndex(null);
    setSelectedSuggestion(null);
  };

  const handleSuggestionSelect = (suggestion: string): void => {
    const isAlreadySelected = suggestion === selectedSuggestion;
    const nextSelected: string | null = isAlreadySelected ? null : suggestion;

    setSelectedSuggestion(nextSelected);
    setHighlightedIndex(null);

    if (onSuggestionSelect) {
      onSuggestionSelect(nextSelected);
    }

    if (nextSelected !== null) {
      triggerSearch(nextSelected);
    }
  };

  const syncEdgeState = useCallback(
    (swiper: SwiperType) => {
      const { isBeginning, isEnd } = getEdgeState(swiper);
      setEdgeState(prev =>
        prev.isBeginning === isBeginning && prev.isEnd === isEnd ? prev : { isBeginning, isEnd },
      );
      handleOnResize({ ...swiper, isBeginning, isEnd } as SwiperType);
    },
    [handleOnResize],
  );

  useEffect(() => {
    if (!isSuggestionsVisible) return;

    const rafId = requestAnimationFrame(() => {
      if (swiperRef.current) {
        syncEdgeState(swiperRef.current);
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [suggestions, isSuggestionsVisible, syncEdgeState, swiperRef]);

  const { isBeginning, isEnd } = edgeState;

  return (
    <div className={classNames('vds-search-bar', className)}>
      <div className="vds-search-bar__control">
        <span className="vds-search-bar__icon">
          <Icon name="search" size="medium" />
        </span>
        <input
          className="vds-search-bar__input vds-typography-subtitle-02-regular-large"
          type="search"
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          aria-label={inputAriaLabel}
          {...rest}
        />
        {inputValue && (
          <button
            type="button"
            className="vds-search-bar__clear"
            onClick={handleClear}
            aria-label={clearButtonAriaLabel}>
            <Icon name="close" size="medium" />
          </button>
        )}
      </div>

      {isSuggestionsVisible && suggestions.length > 0 && (
        <div
          className={classNames('vds-search-bar__row', {
            'vds-search-bar--disabled': isSwiperDisabled,
          })}>
          <div className="vds-search-bar__row-scroll">
            <Swiper
              slidesPerView="auto"
              watchOverflow
              resistanceRatio={0}
              onAfterInit={syncEdgeState}
              onResize={syncEdgeState}
              onSlideChangeTransitionEnd={syncEdgeState}
              onSwiper={swiper => {
                swiperRef.current = swiper;
              }}
              className="vds-search-bar__row-swiper">
              {suggestions.map((suggestion, index) => {
                const isHighlighted = index === highlightedIndex;
                const isSelected = suggestion === selectedSuggestion;

                return (
                  <SwiperSlide
                    key={`${suggestion}-${index}`}
                    className="vds-search-bar__slide"
                    style={{ width: 'auto' }}>
                    <button
                      type="button"
                      className={classNames('vds-search-bar__chip', {
                        'vds-search-bar__chip--highlighted': isHighlighted || isSelected,
                      })}
                      data-ci={suggestionDataCi}
                      onMouseDown={event => event.preventDefault()}
                      onClick={() => handleSuggestionSelect(suggestion)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onMouseLeave={() => setHighlightedIndex(null)}>
                      {suggestion}
                    </button>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
          <div className="vds-search-bar__row-arrows">
            <button
              type="button"
              onClick={() => swiperRef.current?.slidePrev()}
              className="vds-search-bar__arrow vds-search-bar__arrow--prev"
              aria-label={prevSuggestionsAriaLabel}
              disabled={isBeginning}>
              <Icon name="chevron-backward" size="large" />
            </button>
            <button
              type="button"
              onClick={() => swiperRef.current?.slideNext()}
              className="vds-search-bar__arrow vds-search-bar__arrow--next"
              aria-label={nextSuggestionsAriaLabel}
              disabled={isEnd}>
              <Icon name="chevron-forward" size="large" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
